import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Trash2, Plus, Minus, ShoppingBag, ArrowRight, CheckCircle2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { api, resolveImg } from "../lib/api";
import { sessionId } from "../lib/usePresence";

export const CartDrawer = () => {
  const { items, remove, setQty, clear, open, setOpen } = useCart();
  const [step, setStep] = useState("cart"); // cart | form | done
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const close = () => { setOpen(false); setTimeout(() => setStep("cart"), 300); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Indica o nome e o email."); return; }
    setSending(true);
    try {
      await api.post("/orders", {
        ...form,
        // Lets the products asked about be mentioned to other visitors,
        // while leaving this tab out of the count it is shown.
        session: sessionId(),
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          quantity: i.quantity,
          variant: i.variant || "",
          customization: i.customization || "",
        })),
      });
      setStep("done");
      clear();
    } catch (err) {
      toast.error("Não foi possível enviar o pedido. Tenta novamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close} data-testid="cart-overlay"
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            data-testid="cart-drawer"
            className="fixed top-0 right-0 z-50 h-full w-full max-w-md bg-background flex flex-col shadow-2xl"
          >
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="font-display font-bold text-xl flex items-center gap-2">
                <ShoppingBag size={20} /> {step === "form" ? "Os teus dados" : step === "done" ? "Pedido enviado" : "Pedido de orçamento"}
              </h2>
              <button onClick={close} data-testid="cart-close" className="p-2 rounded-full hover:bg-muted"><X size={20} /></button>
            </div>

            {step === "cart" && (
              <>
                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                  {items.length === 0 ? (
                    <div className="text-center text-muted-foreground py-20">
                      <ShoppingBag size={40} className="mx-auto mb-4 opacity-40" />
                      <p>O teu pedido está vazio.</p>
                      <p className="text-sm mt-1">Adiciona produtos para pedires um orçamento.</p>
                    </div>
                  ) : items.map((i) => (
                    <div key={i.key} data-testid={`cart-item-${i.key}`} className="flex gap-3 items-start bg-card border border-border rounded-2xl p-3">
                      <div className="w-16 h-16 rounded-xl bg-muted overflow-hidden shrink-0">
                        {i.image && <img src={resolveImg(i.image)} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{i.name}</p>
                        {i.variant && (
                          <p data-testid={`cart-variant-${i.key}`} className="text-xs font-semibold text-primary mt-0.5">
                            {i.variant}
                          </p>
                        )}
                        {i.customization && (
                          <p data-testid={`cart-customization-${i.key}`} className="text-xs text-muted-foreground mt-1 line-clamp-2 break-words">
                            <Pencil size={11} className="inline mr-1 -mt-0.5" />{i.customization}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <button onClick={() => setQty(i.key, i.quantity - 1)} aria-label="Menos" className="w-7 h-7 grid place-items-center rounded-full border border-border hover:bg-muted"><Minus size={13} /></button>
                          <span data-testid={`cart-qty-${i.key}`} className="w-6 text-center font-semibold text-sm">{i.quantity}</span>
                          <button onClick={() => setQty(i.key, i.quantity + 1)} aria-label="Mais" className="w-7 h-7 grid place-items-center rounded-full border border-border hover:bg-muted"><Plus size={13} /></button>
                        </div>
                      </div>
                      <button onClick={() => remove(i.key)} data-testid={`cart-remove-${i.key}`} aria-label="Remover" className="p-2 rounded-full hover:bg-destructive/10 text-destructive"><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
                {items.length > 0 && (
                  <div className="p-5 border-t border-border">
                    <button onClick={() => setStep("form")} data-testid="cart-checkout-btn"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full brand-gradient text-white font-bold py-4 transition-transform hover:scale-[1.02] active:scale-95">
                      Pretendo Comprar <ArrowRight size={18} />
                    </button>
                    <p className="text-xs text-muted-foreground text-center mt-2">Sem pagamento agora — enviamos-te o orçamento.</p>
                  </div>
                )}
              </>
            )}

            {step === "form" && (
              <form onSubmit={submit} data-testid="cart-form" className="flex-1 overflow-y-auto p-5 flex flex-col">
                <p className="text-sm text-muted-foreground mb-4">Deixa os teus dados e entramos em contacto com o orçamento para os {items.length} produto(s) selecionado(s).</p>
                <label className="text-sm font-semibold">Nome *</label>
                <input data-testid="order-name" value={form.name} onChange={(e) => set("name", e.target.value)} required className="w-full mt-1 mb-3 rounded-2xl border border-border bg-card px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40" />
                <label className="text-sm font-semibold">Email *</label>
                <input data-testid="order-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="w-full mt-1 mb-3 rounded-2xl border border-border bg-card px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40" />
                <label className="text-sm font-semibold">Telemóvel / WhatsApp</label>
                <input data-testid="order-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="w-full mt-1 mb-3 rounded-2xl border border-border bg-card px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40" />
                <label className="text-sm font-semibold">Mensagem</label>
                <textarea data-testid="order-message" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Cores, personalizações, quantidades..." className="w-full mt-1 mb-4 rounded-2xl border border-border bg-card px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40 resize-none" />
                <div className="mt-auto flex gap-3">
                  <button type="button" onClick={() => setStep("cart")} className="rounded-full border border-border px-5 py-3 font-semibold hover:bg-muted">Voltar</button>
                  <button type="submit" data-testid="order-submit" disabled={sending} className="flex-1 rounded-full brand-gradient text-white font-bold py-3 transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60">
                    {sending ? "A enviar..." : "Enviar pedido"}
                  </button>
                </div>
              </form>
            )}

            {step === "done" && (
              <div data-testid="cart-done" className="flex-1 grid place-items-center p-8 text-center">
                <div>
                  <CheckCircle2 size={56} className="mx-auto text-[#7ED321] mb-4" />
                  <h3 className="font-display font-bold text-2xl">Pedido enviado!</h3>
                  <p className="text-muted-foreground mt-2">Recebemos o teu pedido de orçamento e entraremos em contacto muito em breve. Obrigado! ❤</p>
                  <button onClick={close} className="mt-6 rounded-full brand-gradient text-white font-bold px-8 py-3">Fechar</button>
                </div>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
